import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { updateArrayItem } from 'lib/helpers';
import { TenantAccess } from 'components/merchant/models/TenantAccess';
import { tenantAccessApi } from 'components/merchant/tenantAccessApi';
import { enqueueSnackbar } from 'notistack';
import type { AppDispatch } from '../store';

const TENANT_ACCESS_PER_PAGE = 100;

interface TenantAccessSummary {
    count: number;
    limit: number;
    nextKey?: string;
}

export interface TenantAccessState {
    tenantUsersAccess?: TenantAccess[];
    error?: string;
    isLoading: boolean;
    summary?: TenantAccessSummary;
}

export const initialState: TenantAccessState = {
    tenantUsersAccess: null,
    error: null,
    isLoading: false
};

const tenantAccessSlice = createSlice({
    name: 'tenantAccess',
    initialState,
    reducers: {
        startRequest: state => {
            state.error = null;
            state.isLoading = true;
        },
        endRequest: state => {
            state.error = null;
            state.isLoading = false;
        },
        getTenantUsersAccessSuccess: (
            state,
            action: PayloadAction<{ tenantUsersAccess: TenantAccess[]; summary: TenantAccessSummary }>
        ) => {
            state.tenantUsersAccess = action.payload.tenantUsersAccess;
            state.summary = action.payload.summary;
        },
        loadTenantUsersAccessSuccess: (
            state,
            action: PayloadAction<{ tenantUsersAccess: TenantAccess[]; summary: TenantAccessSummary }>
        ) => {
            state.tenantUsersAccess = [...state.tenantUsersAccess, ...action.payload.tenantUsersAccess];
            state.summary = action.payload.summary;
        },
        getTenantUsersAccessError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.tenantUsersAccess = null;
        },
        updateTenantUserAccessSuccess: (
            state,
            action: PayloadAction<{ index: number; tenantAccess: TenantAccess }>
        ) => {
            state.tenantUsersAccess = updateArrayItem(
                state.tenantUsersAccess,
                action.payload.index,
                action.payload.tenantAccess
            );
        },
        updateTenantUserAccessError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
        },
        addTenantUserAccessSuccess: (state, action: PayloadAction<TenantAccess>) => {
            state.tenantUsersAccess = [...state.tenantUsersAccess, action.payload];
        },
        addTenantUserAccessError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
        }
    }
});

export const {
    startRequest,
    endRequest,
    getTenantUsersAccessSuccess,
    loadTenantUsersAccessSuccess,
    getTenantUsersAccessError,
    updateTenantUserAccessSuccess,
    updateTenantUserAccessError,
    addTenantUserAccessSuccess,
    addTenantUserAccessError
} = tenantAccessSlice.actions;

// Async thunks

export const getTenantUsersAccess = () => async (dispatch: AppDispatch) => {
    dispatch(startRequest());
    const queryParameters = [{ key: 'limit', value: TENANT_ACCESS_PER_PAGE }];
    const response = await tenantAccessApi.getListByTenant(queryParameters);
    dispatch(endRequest());
    if (response.ok) {
        const tenantUsersAccess = response.body;
        dispatch(
            getTenantUsersAccessSuccess({
                tenantUsersAccess: tenantUsersAccess.items,
                summary: tenantUsersAccess.page
            })
        );
    }
};

export const loadTenantUserAccess = (startKey: string) => async (dispatch: AppDispatch) => {
    dispatch(startRequest());
    const queryParameters = [
        { key: 'limit', value: TENANT_ACCESS_PER_PAGE },
        { key: 'startKey', value: startKey }
    ];
    const response = await tenantAccessApi.getListByTenant(queryParameters);
    if (!response.ok) {
        dispatch(getTenantUsersAccessError(undefined));
    } else {
        dispatch(
            loadTenantUsersAccessSuccess({
                tenantUsersAccess: response.body.items,
                summary: response.body.page
            })
        );
    }
    dispatch(endRequest());
};

export const revokeUsersAccess = (id: string, index: number) => async (dispatch: AppDispatch) => {
    dispatch(startRequest());
    const result = await tenantAccessApi.revokeUserAccess({ id });
    if (result.ok) {
        dispatch(updateTenantUserAccessSuccess({ index, tenantAccess: result.body }));
        enqueueSnackbar(`Successfully revoked access for ${result.body.email}`, {
            variant: 'success'
        });
    } else {
        dispatch(updateTenantUserAccessError((result.body as any).message));
        enqueueSnackbar(`Error revoking user access: ${(result.body as any).message}`, {
            variant: 'error'
        });
    }
    dispatch(endRequest());
};

export const updateUsersAccess = (index: number, tenantAccess: TenantAccess) => (dispatch: AppDispatch) => {
    dispatch(updateTenantUserAccessSuccess({ index, tenantAccess }));
};

export const addUsersAccess = (tenantAccess: TenantAccess) => (dispatch: AppDispatch) => {
    dispatch(addTenantUserAccessSuccess(tenantAccess));
};

export default tenantAccessSlice.reducer;
